/**
 * Display formatting for the whole app. Every amount, date, phone number and area goes
 * through here so the UI never shows ₹NaN, "Invalid Date" or 125,000 instead of 1,25,000.
 *
 * Rules (docs/BRANDING_AND_DESIGN_SYSTEM.md): INR with Indian digit grouping, metric
 * units (m²), and all business dates in Asia/Kolkata.
 */

export const IST = 'Asia/Kolkata';
const DASH = '—';

const inrWhole = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});
const inrExact = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const plainNumber = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Round to 2 decimal places without float drift (1.005 -> 1.01). */
export function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Format rupees with Indian grouping. Whole rupees show no decimals; pass
 * `{ exact: true }` for ledgers and receipts that must show paise.
 */
export function formatINR(
  value: number | null | undefined,
  options: { exact?: boolean } = {},
): string {
  if (!isFiniteNumber(value)) {
    return DASH;
  }
  const rounded = roundMoney(value);
  if (options.exact) {
    return inrExact.format(rounded);
  }
  return Number.isInteger(rounded) ? inrWhole.format(rounded) : inrExact.format(rounded);
}

/** Compact rupees for KPI tiles: ₹8,500 · ₹2.45 L · ₹1.20 Cr. */
export function formatCompactINR(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) {
    return DASH;
  }
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1_00_00_000) {
    return `${sign}₹${(abs / 1_00_00_000).toFixed(2)} Cr`;
  }
  if (abs >= 1_00_000) {
    return `${sign}₹${(abs / 1_00_000).toFixed(2)} L`;
  }
  return formatINR(value);
}

export function formatNumber(value: number | null | undefined): string {
  return isFiniteNumber(value) ? plainNumber.format(value) : DASH;
}

export function formatPercent(value: number | null | undefined, digits = 0): string {
  return isFiniteNumber(value) ? `${value.toFixed(digits)}%` : DASH;
}

/** Floor areas are metric only: 24.5 m². */
export function formatArea(squareMetres: number | null | undefined): string {
  return isFiniteNumber(squareMetres) ? `${plainNumber.format(squareMetres)} m²` : DASH;
}

type DateInput = string | number | Date | null | undefined;

function toDate(input: DateInput): Date | null {
  if (input === null || input === undefined || input === '') {
    return null;
  }
  const date = input instanceof Date ? input : new Date(input);
  return Number.isNaN(date.getTime()) ? null : date;
}

const dateShort = new Intl.DateTimeFormat('en-IN', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: IST,
});
const dateNoYear = new Intl.DateTimeFormat('en-IN', {
  day: 'numeric',
  month: 'short',
  timeZone: IST,
});
const dateTime = new Intl.DateTimeFormat('en-IN', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZone: IST,
});
const timeOnly = new Intl.DateTimeFormat('en-IN', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZone: IST,
});
const monthYear = new Intl.DateTimeFormat('en-IN', {
  month: 'long',
  year: 'numeric',
  timeZone: IST,
});

export function formatDate(input: DateInput): string {
  const date = toDate(input);
  return date ? dateShort.format(date) : DASH;
}

export function formatDayMonth(input: DateInput): string {
  const date = toDate(input);
  return date ? dateNoYear.format(date) : DASH;
}

export function formatDateTime(input: DateInput): string {
  const date = toDate(input);
  return date ? dateTime.format(date) : DASH;
}

export function formatTime(input: DateInput): string {
  const date = toDate(input);
  return date ? timeOnly.format(date) : DASH;
}

export function formatMonthYear(input: DateInput): string {
  const date = toDate(input);
  return date ? monthYear.format(date) : DASH;
}

const relative = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/** "today", "in 3 days", "2 weeks ago". */
export function formatRelative(input: DateInput, now: Date = new Date()): string {
  const date = toDate(input);
  if (!date) {
    return DASH;
  }
  const days = Math.round((startOfDayIST(date) - startOfDayIST(now)) / 86_400_000);
  if (Math.abs(days) < 7) {
    return relative.format(days, 'day');
  }
  if (Math.abs(days) < 60) {
    return relative.format(Math.round(days / 7), 'week');
  }
  return relative.format(Math.round(days / 30), 'month');
}

/** Midnight IST for a date, as epoch ms, so day maths ignores the viewer's timezone. */
function startOfDayIST(date: Date): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: IST,
  }).format(date);
  return Date.parse(`${parts}T00:00:00+05:30`);
}

/** Whole days from `from` to `to` (negative when `to` is earlier), in IST. */
export function daysBetween(from: DateInput, to: DateInput): number | null {
  const a = toDate(from);
  const b = toDate(to);
  if (!a || !b) {
    return null;
  }
  return Math.round((startOfDayIST(b) - startOfDayIST(a)) / 86_400_000);
}

/** True when the due date is before today (IST). */
export function isPastDue(dueDate: DateInput, now: Date = new Date()): boolean {
  const days = daysBetween(now, dueDate);
  return days !== null && days < 0;
}

/** `YYYY-MM-DD` for <input type="date">, in IST. */
export function toDateInputValue(input: DateInput = new Date()): string {
  const date = toDate(input);
  if (!date) {
    return '';
  }
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: IST,
  }).format(date);
}

/** Digits only, last 10 (drops +91 / 0 prefixes). */
export function toTenDigitPhone(phone: string | null | undefined): string {
  if (!phone) {
    return '';
  }
  return phone.replace(/\D/g, '').slice(-10);
}

/** Indian mobile numbers are 10 digits starting 6-9. */
export function isValidIndianMobile(phone: string): boolean {
  return /^[6-9]\d{9}$/.test(toTenDigitPhone(phone)) && phone.replace(/\D/g, '').length >= 10;
}

/** +919876543210 -> +91 98765 43210. */
export function formatPhone(phone: string | null | undefined): string {
  const ten = toTenDigitPhone(phone);
  if (ten.length !== 10) {
    return phone?.trim() ? phone : DASH;
  }
  return `+91 ${ten.slice(0, 5)} ${ten.slice(5)}`;
}

/** Aadhaar is never shown in full: XXXX-XXXX-1234. */
export function maskAadhaar(value: string | null | undefined): string {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits.length >= 4 ? `XXXX-XXXX-${digits.slice(-4)}` : DASH;
}

export function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`;
}

/** SNAKE_CASE or kebab-case enum -> "Snake case". */
export function humanize(value: string | null | undefined): string {
  if (!value) {
    return DASH;
  }
  const text = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}
